import React, { useState, useEffect } from "react";
import {
  Container,
  PageContainer,
  PageHeader,
  PageTitle,
} from "../../../styles/components";
import StudentsList from "../../../components/scadOffice/StudentsList/StudentsList";
import StudentProfile from "../../../components/scadOffice/StudentProfile/StudentProfile";
import ReportsList from "../../../components/scadOffice/ReportsList/ReportsList";
import CompaniesList from "../../../components/scadOffice/CompaniesList/CompaniesList";
import DashboardNav from "../../../components/scadOffice/DashboardNav/DashboardNav";
import CycleManagement from "../../../components/scadOffice/CycleManagement/CycleManagement";
import StatisticsCards from "../../../components/scadOffice/StatisticsCards/StatisticsCards";
import DetailedStatisticsCharts from "../../../components/scadOffice/DetailedStatisticsCharts/DetailedStatisticsCharts";
import ActivityList from "../../../components/scadOffice/ActivityList/ActivityList";
import ApprovalsList from "../../../components/scadOffice/ApprovalsList/ApprovalsList";
import SettingsPanel from "../../../components/scadOffice/SettingsPanel/SettingsPanel";
import WorkshopsManagement from "../../../components/scadOffice/WorkshopsManagement/WorkshopsManagement";
import { CardGrid } from "../../../styles/components/ModernCard";
import styled, { keyframes } from "styled-components";
import VideoCallSystemDemo from "../../../components/scadOffice/VideoCallSystem/VideoCallSystemDemo";

// Space-themed styled components with explicit animation definitions
const titleGlow = keyframes`
  0% { text-shadow: 0 0 10px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3); }
  50% { text-shadow: 0 0 20px rgba(99, 102, 241, 0.8), 0 0 30px rgba(99, 102, 241, 0.5); }
  100% { text-shadow: 0 0 10px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const StyledPageContainer = styled(PageContainer)`
  position: relative;
  overflow: hidden;
  min-height: unset;
  padding: 2rem 0;
`;

const GlowingPageTitle = styled(PageTitle)`
  color: rgba(255, 255, 255, 0.95);
  position: relative;
  animation: ${titleGlow} 4s infinite ease-in-out,
    ${floatAnimation} 6s infinite ease-in-out;
  letter-spacing: 1px;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), transparent);
    border-radius: 1px;
  }
`;

const SpaceBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  background-image: radial-gradient(
      1px 1px at 5% 5%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 25% 20%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 50% 5%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 75% 25%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 95% 15%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 10% 40%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 30% 65%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 55% 45%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 80% 70%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 90% 55%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 15% 80%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 35% 95%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 60% 85%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 85% 90%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 95% 80%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    );
  opacity: 0.3;
`;

// Styled Components - exported to be reused in other components
export const WelcomeSection = styled.div`
  margin-bottom: 2rem;
  padding: 2rem;
  background: linear-gradient(
    135deg,
    rgba(41, 121, 255, 0.2) 0%,
    rgba(77, 171, 247, 0.3) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 16px;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.18);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 10% 20%,
        rgba(28, 175, 252, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 30%,
        rgba(117, 91, 255, 0.15) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 30% 70%,
        rgba(252, 99, 251, 0.1) 0%,
        transparent 40%
      );
    z-index: -1;
    background: url("https://cdn-icons-png.flaticon.com/512/2534/2534204.png")
      no-repeat;
    background-size: contain;
    background-position: right center;
    opacity: 0.1;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 65%,
      rgba(255, 255, 255, 0.08) 75%,
      transparent 85%
    );
    animation: lightSweep 8s infinite linear;
    z-index: 1;
    pointer-events: none;
  }

  @keyframes lightSweep {
    0% {
      transform: rotate(0deg) translateX(0%);
    }
    100% {
      transform: rotate(360deg) translateX(10%);
    }
  }
`;

export const WelcomeTitle = styled.h2`
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.8rem 0;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-shadow: 0 0 15px rgba(114, 137, 218, 0.5);
  position: relative;
  z-index: 2;
`;

export const WelcomeText = styled.p`
  font-size: 1.1rem;
  margin: 0;
  max-width: 600px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  position: relative;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.8), transparent);
    border-radius: 1px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 1.5rem 0;
  position: relative;
  display: inline-block;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), transparent);
    border-radius: 1px;
  }
`;

const ScadOfficeDashboard = () => {
  const [activeView, setActiveView] = useState("dashboard");
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [cycleStartDate, setCycleStartDate] = useState("2025-05-01");
  const [cycleEndDate, setCycleEndDate] = useState("2025-08-31");
  const [isEditingCycle, setIsEditingCycle] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Function to handle viewing a student profile
  const handleViewStudentProfile = (studentId) => {
    setSelectedStudentId(studentId);
    setActiveView("studentProfile");
  };

  // Function to go back to students list
  const handleBackToStudents = () => {
    setSelectedStudentId(null);
    setActiveView("students");
  };

  // Function to save internship cycle dates
  const saveCycleDates = () => {
    // In a real application, this would update the dates in the database
    console.log(
      `Setting internship cycle: ${cycleStartDate} to ${cycleEndDate}`
    );
    setIsEditingCycle(false);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Mock data for statistics cards
  const statisticsData = [
    { value: "152", label: "Active Students" },
    { value: "43", label: "Partner Companies" },
    { value: "87", label: "Active Internships" },
    { value: "24", label: "Pending Reports" },
  ];

  // Mock data for detailed statistics
  const reportStatistics = {
    accepted: 43,
    rejected: 12,
    flagged: 7,
  };

  const reviewTimeStatistics = {
    May: 3.2,
    June: 2.5,
    July: 2.1,
    August: 1.8,
    September: 1.5,
  };

  const topCoursesStatistics = {
    "Software Engineering": 28,
    "Database Systems": 22,
    "Web Development": 18,
    "Computer Networks": 15,
    "AI & Machine Learning": 12,
  };

  const companyRatingStatistics = {
    "Tech Solutions Inc.": 4.8,
    "Global Systems LLC": 4.6,
    "Digital Innovations": 4.5,
    "Future Tech": 4.3,
    "Smart Development": 4.2,
  };

  const internshipCountStatistics = {
    "Tech Solutions Inc.": 14,
    "Global Systems LLC": 12,
    "Digital Innovations": 9,
    "Future Tech": 8,
    "Smart Development": 7,
  };

  // Mock data for recent activities
  const recentActivities = [
    {
      type: "company",
      title: "New company registration: Tech Solutions Inc.",
      time: "Today, 10:25 AM",
    },
    {
      type: "report",
      title: "Student Ahmed Mohamed submitted a new report",
      time: "Today, 09:15 AM",
    },
    {
      type: "company",
      title: "5 new internship opportunities posted",
      time: "Yesterday, 04:30 PM",
    },
    {
      type: "approval",
      title: "Faculty member approved 3 internship reports",
      time: "Yesterday, 02:45 PM",
    },
    {
      type: "student",
      title: "PRO student status granted to 2 students",
      time: "May 8, 2025",
    },
  ];

  // Mock data for pending approvals
  const pendingApprovals = [
    {
      type: "companyReg",
      title: "Company Registrations",
      count: "3 awaiting approval",
    },
    { type: "reports", title: "Internship Reports", count: "12 need review" },
    {
      type: "proStatus",
      title: "PRO Status Requests",
      count: "5 students requesting verification",
    },
    {
      type: "profileUpdates",
      title: "Company Profile Updates",
      count: "2 companies requesting changes",
    },
  ];

  // Render the appropriate view based on activeView state
  const renderView = () => {
    switch (activeView) {
      case "students":
        return <StudentsList onViewProfile={handleViewStudentProfile} />;
      case "studentProfile":
        return (
          <StudentProfile
            studentId={selectedStudentId}
            onBack={handleBackToStudents}
          />
        );
      case "reports":
        return <ReportsList />;
      case "companies":
        return <CompaniesList />;
      case "workshops":
        return <WorkshopsManagement />;
      case "calls":
        return <VideoCallSystemDemo />;
      case "settings":
        return (
          <SettingsPanel
            isEditingCycle={isEditingCycle}
            cycleStartDate={cycleStartDate}
            cycleEndDate={cycleEndDate}
            formatDate={formatDate}
            onStartDateChange={(value) => setCycleStartDate(value)}
            onEndDateChange={(value) => setCycleEndDate(value)}
            onSaveCycle={saveCycleDates}
            onCancelEdit={() => setIsEditingCycle(false)}
            onStartEdit={() => setIsEditingCycle(true)}
            onArchiveData={() => console.log("Archiving old data...")}
            onGenerateReport={() => console.log("Generating system report...")}
            onBackupSystem={() => console.log("Backing up system...")}
          />
        );
      case "dashboard":
      default:
        return (
          <>
            <WelcomeSection>
              <WelcomeTitle>Welcome to SCAD Office Dashboard</WelcomeTitle>
              <WelcomeText>
                Manage student internships, company partnerships, and monitor
                the overall internship program from this central dashboard.
              </WelcomeText>
            </WelcomeSection>

            <CycleManagement
              startDate={cycleStartDate}
              endDate={cycleEndDate}
              formatDate={formatDate}
              onOpenSettings={() => setActiveView("settings")}
            />

            <StatisticsCards stats={statisticsData} />

            <SectionTitle>Detailed Analytics</SectionTitle>

            <DetailedStatisticsCharts
              reportStats={reportStatistics}
              reviewTimeStats={reviewTimeStatistics}
              courseStats={topCoursesStatistics}
              companyRatingStats={companyRatingStatistics}
              internshipCountStats={internshipCountStatistics}
            />

            <SectionTitle>Recent Activity & Approvals</SectionTitle>
            <CardGrid columns={2}>
              <ActivityList
                activities={recentActivities}
                date={currentTime.toLocaleDateString()}
              />

              <ApprovalsList
                approvals={pendingApprovals}
                onProcessAll={() => setActiveView("reports")}
                onReview={(type) => console.log(`Reviewing ${type}...`)}
              />
            </CardGrid>
          </>
        );
    }
  };

  return (
    <StyledPageContainer>
      <SpaceBackground />
      <Container>
        <PageHeader>
          <GlowingPageTitle>SCAD Office Dashboard</GlowingPageTitle>
        </PageHeader>

        <DashboardNav activeView={activeView} onChangeView={setActiveView} />

        {renderView()}
      </Container>
    </StyledPageContainer>
  );
};

export default ScadOfficeDashboard;
